import { useTranslation } from 'react-i18next';
import { Select, Modal, Descriptions, Spin, Button, Row, Col } from 'antd';
import { useEffect, useState, useCallback } from 'react';
import dayjs from 'dayjs';
import { useLocalData } from '../../../../hooks/useLocalData';
import { useNotificationContext } from '../../../../contexts/NotificationContext';
import { referenceDataSyncService } from '../../../../services/referenceDataSyncService';
import DataTable from '../../../../components/common/DataTable';
import {
  downloadAllInspectionDetails,
  getAllInspectionDetails,
  getAllInspectionDetailsById,
} from '../../getData';
import { normalize } from '../../../../utils/normalizeWords';
import PrimaryButton from '../../../../components/buttons/PrimaryButton';
import { downloadCSVFromUrl } from '../../../../helpers/utilityHelper';
import { TaskTypeEnum, LegalMetroTaskTypeEnum } from '../../../../enums/TaskTypeEnum';
import ViewRecordedMonitoringVisitModal from '../../../subtaskInspections/scaleVerificationInspections/ViewRecordedMonitoringVisit';
import ViewRecordedFuelDispenserVerificationModal from '../../../subtaskInspections/fuelPumpVerificationInspections/ViewRecordedFuelDispenserVerification';
import ViewPrePackagedProductModal from '../../../subtaskInspections/prePackagedProductVerify/ViewPrePackagedProductModal';
import { PriceStatusEnum } from '../../../../enums/PriceStatusEnum';
import { useIsOnlineContext } from '../../../../contexts/IsOnlineContext';
import { API_ENDPOINTS } from '../../../../constants/apiEndpoints';

const AllView = () => {
  const { t } = useTranslation(['inspector', 'price']);

  const { data: inspections } = useLocalData({
    entity: 'inspection',
    autoSync: true,
    sortBy: 'name',
    sortOrder: 'asc',
  });

  const [inspectionData, setInspectionData] = useState<any>(inspections);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [isLoadingInspections, setIsLoadingInspections] = useState<boolean>(false);
  const [pageSize] = useState<number>(8);
  const [totalInspections, setTotalInspections] = useState<number>(0);
  const [isViewModalVisible, setIsViewModalVisible] = useState<boolean>(false);
  const [selectedInspection, setSelectedInspection] = useState<any>(null);
  const [isLoadingInspectionDetails, setIsLoadingInspectionDetails] = useState<boolean>(false);
  const [isdownloadingInspections, setIsdownloadingInspections] = useState<boolean>(false);
  const { errorNotification } = useNotificationContext();

  const [selectedTaskNames, setSelectedTaskNames] = useState<string[]>([]);
  const [selectedEstablishmentType, setSelectedEstablishmentType] = useState<string | undefined>(
    undefined
  );
  const [selectedEstablishment, setSelectedEstablishment] = useState<string | undefined>(undefined);
  const [selectedTaskTypeFilter, setSelectedTaskTypeFilter] = useState<string | undefined>(
    undefined
  );
  const [selectedEstablishmentIds, setSelectedEstablishmentIds] = useState<number[]>([]);
  const [taskTypeOptions, setTaskTypeOptions] = useState<any>();
  const [establishmentData, setEstablishmentData] = useState<
    Array<{ positionType: string; name: string; id: number }>
  >([]);

  const [taskData, setTaskData] = useState<any[]>([]);
  const [establishmentTypes, setEstablishmentTypes] = useState<{ label: string; value: string }[]>(
    []
  );
  const [establishments, setEstablishments] = useState<string[]>([]);
  const [isLoadingFilterData, setIsLoadingFilterData] = useState<boolean>(false);
  const [enableClearFiltersBtn, setEnableClearFiltersBtn] = useState<boolean>(false);
  const { isOnline } = useIsOnlineContext();

  const enrichPriceWithProductInspection = useCallback(async (price: any) => {
    try {
      let productData = null;
      if (price?.product_ref_id) {
        productData = await referenceDataSyncService.getLocalProduct(
          price.product_ref_id as string
        );
      }

      let inspectionData = null;
      if (price?.inspectionRefId) {
        inspectionData = await referenceDataSyncService.getInspectionsByRefId(
          price.inspectionRefId as string
        );
      }

      let locationData = null;
      if (price.location_id) {
        locationData = await referenceDataSyncService.getLocationById(price.location_id);
      }

      const enrichedData = {
        ...price,
        product: productData,
        inspection: {
          ...inspectionData,
          location: locationData,
        },
      };

      return enrichedData;
    } catch (error) {
      console.error('Error enriching price data:', error);
      return price;
    }
  }, []);

  const getProductInspectionDataById = useCallback(
    async (prices: any) => {
      try {
        const priceWithProductData = await Promise.all(
          prices.map(enrichPriceWithProductInspection)
        );

        setInspectionData(priceWithProductData);
      } catch (error) {
        console.error('Error enriching prices with product data:', error);
        setInspectionData(prices);
      }
    },
    [enrichPriceWithProductInspection]
  );

  const getAllInspectionList = useCallback(
    async (page: number = currentPage, filters?: any) => {
      setIsLoadingInspections(true);
      try {
        const paginationParams = {
          page,
          limit: pageSize,
        };

        const filterParams: any = {
          ...paginationParams,
        };

        if (
          filters?.taskNames &&
          Array.isArray(filters?.taskNames) &&
          filters?.taskNames.length > 0
        ) {
          filterParams.taskNames = filters?.taskNames;
        }

        if (
          filters?.establishmentIds &&
          Array.isArray(filters?.establishmentIds) &&
          filters?.establishmentIds.length > 0
        ) {
          filterParams.establishmentIds = filters?.establishmentIds;
        }

        if (filters?.taskType) {
          filterParams.taskType = filters?.taskType;
        }
        const response = await getAllInspectionDetails(filterParams);

        if (response.statusText === 'SUCCESS') {
          const inspections = response.data?.data || response.data || [];
          const total = response.data?.total || 0;
          setInspectionData(inspections);
          setTotalInspections(total);
          setCurrentPage(page);
          // getProductInspectionDataById(inspections);
        } else {
          console.error(t('inspectionDetailFetchError'), response);
        }
      } catch (error) {
        console.error(t('inspectionDetailFetchError'), error);
      } finally {
        setIsLoadingInspections(false);
      }
    },
    [pageSize, errorNotification, t]
  );

  const downloadCSVFn = async () => {
    try {
      setIsdownloadingInspections(true);
      let filterParams: any = {};

      if (selectedTaskNames.length > 0) {
        filterParams.taskNames = selectedTaskNames;
      }

      if (selectedEstablishmentIds.length > 0) {
        filterParams.establishmentIds = selectedEstablishmentIds;
      }

      if (selectedTaskTypeFilter) {
        filterParams.taskType = selectedTaskTypeFilter;
      }
      const response = await downloadAllInspectionDetails(filterParams);
      if (response.statusText === 'SUCCESS') {
        const { url, fileName } = response.data;
        downloadCSVFromUrl(url, fileName);
      } else {
        console.error(t('inspectionDetailDownloadError'), response);
        errorNotification(t('inspectionDetailDownloadError'));
      }
    } catch (error) {
      console.error(t('inspectionDetailDownloadError'), error);
      errorNotification(t('inspectionDetailDownloadError'));
      setIsdownloadingInspections(false);
    } finally {
      setIsdownloadingInspections(false);
    }
  };

  const handlePageChange = (page: number) => {
    const filters = {
      taskNames: selectedTaskNames,
      establishmentIds: selectedEstablishmentIds,
      taskType: selectedTaskTypeFilter,
    };
    getAllInspectionList(page, filters);
  };

  const handleViewInspection = async (inspectionId: string | number) => {
    setIsLoadingInspectionDetails(true);
    setIsViewModalVisible(true);
    try {
      const response = await getAllInspectionDetailsById(inspectionId);

      if (response.statusText === 'SUCCESS' && response.data) {
        setSelectedInspection(response.data);
      } else {
        errorNotification(t('inspectionDetailFetchError'));
      }

      /* const inspection = inspectionData.find((p: any) => p.id === inspectionId);
      if (inspection) {
        setSelectedInspection(inspection);
        } else {
        const response = await fetcher.get(
          `${API_ENDPOINTS.PRICE.PRICE}/${inspectionId}`,
          {},
          {},
          ENVIRONMENT_CONFIG.ANALYSIS_SERVICE
        );

        if (response.statusText === 'SUCCESS' && response.data) {
          setSelectedInspection(response.data);
        } else {
          errorNotification(t('priceDetailFetchError'));
        }
      } */
    } catch (error) {
      console.error(t('inspectionDetailFetchError'), error);
      errorNotification(t('inspectionDetailFetchError'));
    } finally {
      setIsLoadingInspectionDetails(false);
    }
  };

  const handleCloseModal = () => {
    setIsViewModalVisible(false);
    setSelectedInspection(null);
  };

  const fetchFilterData = useCallback(async () => {
    setIsLoadingFilterData(true);
    try {
      const taskData = await referenceDataSyncService.getTasks();
      setTaskData(taskData);
      const taskTypes = await referenceDataSyncService.getTaskTypes();
      setTaskTypeOptions(taskTypes);

      const establishmentData = await referenceDataSyncService.getEstablishments();
      const formattedEstablishmentData = establishmentData.map((establishment: any) => ({
        positionType: (establishment.establishmentType?.key ||
          establishment.establishmentType?.nameEn ||
          establishment.establishmentType ||
          '') as string,
        name: establishment.name as string,
        id: establishment.id as number,
      }));
      setEstablishmentData(formattedEstablishmentData);

      const uniqueTypesMap = new Map<string, string>();
      establishmentData.forEach((est: any) => {
        const typeObj = est.establishmentType;
        if (typeObj && typeof typeObj === 'object' && typeObj.key) {
          uniqueTypesMap.set(typeObj.key, typeObj.nameEn || typeObj.key);
        } else if (typeof typeObj === 'string' && typeObj) {
          uniqueTypesMap.set(typeObj, typeObj);
        }
      });

      setEstablishmentTypes(
        Array.from(uniqueTypesMap.entries()).map(([key, name]) => ({
          label: name,
          value: key,
        }))
      );
    } catch (error) {
      console.error('Error fetching filter data:', error);
    } finally {
      setIsLoadingFilterData(false);
    }
  }, [errorNotification]);

  const clearAllFilters = () => {
    setSelectedTaskNames([]);
    setSelectedEstablishmentType(undefined);
    setSelectedEstablishment(undefined);
    setSelectedTaskTypeFilter(undefined);
    getAllInspectionList(1, {});
  };

  useEffect(() => {
    getAllInspectionList(1);
    fetchFilterData();
  }, [fetchFilterData]);

  useEffect(() => {
    const filters = {
      taskNames: selectedTaskNames,
      establishmentIds: selectedEstablishmentIds,
      taskType: selectedTaskTypeFilter,
    };

    const hasFilters =
      selectedTaskNames.length > 0 || selectedEstablishmentIds.length > 0 || selectedTaskTypeFilter;

    if (hasFilters) {
      getAllInspectionList(1, filters);
      setEnableClearFiltersBtn(true);
    } else {
      getAllInspectionList(1, {});
      setEnableClearFiltersBtn(false);
    }
  }, [selectedTaskNames, selectedEstablishmentIds, selectedTaskTypeFilter]);

  useEffect(() => {
    if (selectedEstablishmentType) {
      const establishmentsForSelectedType = establishmentData
        .filter((establishment) => establishment.positionType === selectedEstablishmentType)
        .map((establishment) => establishment.id);

      setSelectedEstablishmentIds(establishmentsForSelectedType);
      const filteredEstablishments = establishmentData
        .filter((establishment) => establishment.positionType === selectedEstablishmentType)
        .map((establishment) => establishment.name);
      setEstablishments(filteredEstablishments);
      setSelectedEstablishment(undefined);
    } else {
      setEstablishments([]);
      setSelectedEstablishment(undefined);
      setSelectedEstablishmentIds([]);
    }
  }, [selectedEstablishmentType, establishmentData]);

  useEffect(() => {
    if (selectedEstablishment && selectedEstablishmentType) {
      const establishmentIdsForSelectedEstablishment = establishmentData
        .filter(
          (establishment) =>
            establishment.positionType === selectedEstablishmentType &&
            establishment.name === selectedEstablishment
        )
        .map((establishment) => establishment.id);

      setSelectedEstablishmentIds(establishmentIdsForSelectedEstablishment);
    } else if (selectedEstablishment) {
      const establishmentIdsForSelectedEstablishment = establishmentData
        .filter((establishment) => establishment.name === selectedEstablishment)
        .map((establishment) => establishment.id);

      setSelectedEstablishmentIds(establishmentIdsForSelectedEstablishment);
    } else if (selectedEstablishmentType) {
      const establishmentIdsForSelectedType = establishmentData
        .filter((establishment) => establishment.positionType === selectedEstablishmentType)
        .map((establishment) => establishment.id);

      setSelectedEstablishmentIds(establishmentIdsForSelectedType);
    } else {
      setSelectedEstablishmentIds([]);
    }
  }, [selectedEstablishment]);

  // Define table columns
  const columns = [
    {
      key: 'taskName',
      title: t('taskName'),
      dataIndex: 'taskName',
      width: 200,
      render: (value: any, record: any) => (
        <span className="font-medium">
          {record?.task_name ? (
            record?.task_name
          ) : (
            <span className="text-gray-400"> {t('manual')}</span>
          )}
        </span>
      ),
    },
    {
      key: 'taskType',
      title: t('taskType'),
      dataIndex: 'taskType',
      width: 250,
      render: (value: any, record: any) => (
        <span className="font-medium">
          {t(record?.taskType) || t(TaskTypeEnum.CONSUMER_PRICE_INSPECTION)}
        </span>
      ),
    },
    {
      key: 'subTaskType',
      title: t('subTaskType'),
      dataIndex: 'subTaskType',
      width: 250,
      render: (value: any, record: any) => (
        <span className="font-medium">{t(record?.subTaskType) || '-'}</span>
      ),
    },
    {
      key: 'establishmentType',
      title: t('establishmentType'),
      dataIndex: 'establishmentType',
      width: 150,
      render: (value: any, record: any) => {
        const typeStr = record?.establishment_type;
        const matched = establishmentTypes.find((t) => t.value === typeStr);
        return <span className="font-medium">{matched ? matched.label : typeStr || '-'}</span>;
      },
    },
    {
      key: 'establishment',
      title: t('establishment'),
      dataIndex: 'establishment',
      width: 80,
      align: 'left' as const,
      render: (value: any, record: any) => (
        <span className="font-medium">{record?.establishment_name || '-'}</span>
      ),
    },
    {
      key: 'submittedBy',
      title: t('submittedBy'),
      dataIndex: 'submittedBy',
      width: 80,
      align: 'left' as const,
      render: (value: any, record: any) => (
        <span className="font-medium">
          {record?.user_firstName || record?.user_fullName || 'N/A'} {record?.user_lastName}
        </span>
      ),
    },
    {
      key: 'recordedPrices',
      title: t('recordedPrices'),
      dataIndex: 'recordedPrices',
      width: 100,
      align: 'right' as const,
      render: (value: any, record: any) => (
        <span className="font-medium">{record?.price_count || '-'}</span>
      ),
    },
    {
      key: 'reportedTime',
      title: t('reportedTime'),
      dataIndex: 'reportedTime',
      align: 'center' as const,
      width: 200,
      render: (value: any) => (
        <span className="text-sm text-gray-600">
          {value ? dayjs(new Date(Number(value))).format('YYYY-MM-DD hh:mm:ss A') : 'N/A'}
        </span>
      ),
    },
    /* {
      key: 'location',
      title: t('location'),
      dataIndex: 'location',
      width: 200,
      render: (value: any, record: any) => {
        const location = record.inspection?.location;
        if (!location) {
          return <span className="text-gray-600">N/A</span>;
        }
        return (
          <span className="text-sm text-gray-700">
            {location.district && location.city
              ? `${location.district}, ${location.city}`
              : location.name || 'N/A'}
          </span>
        );
      },
    }, */
  ];

  // Define table actions
  const actions = [
    {
      key: 'view',
      label: t('view'),
      type: 'primary' as const,
      onClick: (record: any) => handleViewInspection(record.id),
    },
  ];

  // Define table columns
  const viewProductPricecolumns = [
    {
      key: 'product',
      title: t('product'),
      dataIndex: 'product',
      width: 100,
      render: (value: any, record: any) => (
        <span className="font-medium">{record?.product?.name || 'N/A'}</span>
      ),
    },
    {
      key: 'price',
      title: t('price'),
      dataIndex: 'price',
      width: 50,
      align: 'center' as const,
      render: (value: any, record: any) => (
        <span className="font-medium">
          {record?.status === PriceStatusEnum.PRODUCTNOTFOUND ? (
            <span className="font-semibold">-</span>
          ) : (
            record?.price || '-'
          )}
        </span>
      ),
    },
    {
      key: 'status',
      title: t('availability'),
      dataIndex: 'status',
      width: 50,
      align: 'center' as const,
      render: (value: any, record: any) => (
        <span className="font-medium">
          {record?.status === PriceStatusEnum.PRODUCTNOTFOUND ? (
            <span className="font-semibold">{t('no')}</span>
          ) : (
            <span>{t('yes')}</span>
          )}
        </span>
      ),
    },
    {
      key: 'productRemarks',
      title: t('productRemarks'),
      dataIndex: 'productRemarks',
      width: 80,
      align: 'left' as const,
      render: (value: any, record: any) => (
        <span className="font-medium">{record?.productRemarks || '-'}</span>
      ),
    },
    /* {
      key: 'photoOrInvoice',
      title: t('photoOrInvoice'),
      dataIndex: 'photoOrInvoice',
      width: 80,
      align: 'left' as const,
      render: (value: any, record: any) => (
        <><Image 
          src={
            record?.photoOrInvoice
              ? record?.photoOrInvoice.startsWith('http')
                ? record?.photoOrInvoice
                : `data:image/png;base64,${record?.photoOrInvoice}`
              : '/images/checkers.png'
          }
          className="m-0 mt-1 flex-shrink-0 rounded-md object-cover"
          width={50}
          height={50}
          preview={true}
        />
        </>
      ),
    }, */
  ];

  return (
    <>
      <section className="mb-8 w-full">
        <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-800">{t('filters')}</h3>
            <Button
              type="link"
              onClick={clearAllFilters}
              className="hover:text-primary-dark text-sm text-primary"
              disabled={!enableClearFiltersBtn}
            >
              {t('clearFilters')}
            </Button>
          </div>

          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={8} lg={4}>
              <div className="mb-2">
                <label className="text-sm font-medium text-gray-700">{t('taskName')}</label>
              </div>
              <Select
                className="w-full [&>.ant-select-selector]:!rounded-lg"
                placeholder={t('taskNamePlaceholder')}
                mode="multiple"
                allowClear
                onClear={() => setSelectedTaskNames([])}
                value={selectedTaskNames}
                onChange={setSelectedTaskNames}
                loading={isLoadingFilterData}
                options={taskData.map((task: any) => ({
                  label: task.taskName,
                  value: task.taskName,
                }))}
                showSearch
                filterOption={(input, option) =>
                  normalize(option?.label ?? '')
                    .toString()
                    .toLowerCase()
                    .includes(normalize(input.toLowerCase()))
                }
              />
            </Col>

            <Col xs={24} sm={12} md={8} lg={4}>
              <div className="mb-2">
                <label className="text-sm font-medium text-gray-700">
                  {t('establishmentType')}
                </label>
              </div>
              <Select
                className="w-full [&>.ant-select-selector]:!rounded-lg"
                placeholder={t('establishmentTypePlaceholder')}
                allowClear
                onClear={() => setSelectedEstablishmentType(undefined)}
                value={selectedEstablishmentType || undefined}
                onChange={setSelectedEstablishmentType}
                options={establishmentTypes.map((type) => ({
                  label: type.label,
                  value: type.value,
                }))}
                loading={isLoadingFilterData}
                showSearch
                filterOption={(input, option) =>
                  normalize(option?.label ?? '')
                    .toString()
                    .toLowerCase()
                    .includes(normalize(input.toLowerCase()))
                }
              />
            </Col>

            <Col xs={24} sm={12} md={8} lg={4}>
              <div className="mb-2">
                <label className="text-sm font-medium text-gray-700">{t('establishment')}</label>
              </div>
              <Select
                className="w-full [&>.ant-select-selector]:!rounded-lg"
                placeholder={t('establishmentPlaceholder')}
                allowClear
                onClear={() => setSelectedEstablishment(undefined)}
                value={selectedEstablishment || undefined}
                onChange={setSelectedEstablishment}
                disabled={!selectedEstablishmentType || establishments.length === 0}
                options={establishments.map((establishment) => ({
                  label: establishment,
                  value: establishment,
                }))}
                loading={isLoadingFilterData}
                showSearch
                filterOption={(input, option) =>
                  normalize(option?.label ?? '')
                    .toString()
                    .toLowerCase()
                    .includes(normalize(input.toLowerCase()))
                }
              />
            </Col>
            <Col xs={24} sm={12} md={8} lg={4}>
              <div className="mb-2">
                <label className="text-sm font-medium text-gray-700">{t('taskTypeSelector')}</label>
              </div>
              <Select
                className="w-full [&>.ant-select-selector]:!rounded-lg"
                placeholder={t('taskTypePlaceholder')}
                allowClear
                onClear={() => setSelectedTaskTypeFilter(undefined)}
                value={selectedTaskTypeFilter || undefined}
                onChange={setSelectedTaskTypeFilter}
                options={
                  taskTypeOptions &&
                  taskTypeOptions.map((type: any) => ({
                    label: t(type?.key),
                    value: type?.id,
                  }))
                }
                loading={isLoadingFilterData}
                filterOption={(input, option) =>
                  normalize(String(option?.label ?? ''))
                    .toString()
                    .toLowerCase()
                    .includes(normalize(input.toLowerCase()))
                }
              />
            </Col>
          </Row>
          <Row className="mt-4 text-sm" justify="end" gutter={8}>
            <PrimaryButton
              handleOnClick={downloadCSVFn}
              disabled={isdownloadingInspections || !isOnline}
              className="px-3"
            >
              {t('downloadCSV')}
            </PrimaryButton>
          </Row>
        </div>
      </section>

      <section className="mb-6 w-full">
        <DataTable
          data={inspectionData.flat(2)}
          columns={columns}
          actions={actions}
          loading={isLoadingInspections}
          totalItems={totalInspections}
          currentPage={currentPage}
          pageSize={pageSize}
          onPageChange={handlePageChange}
          rowKey="id"
          showSizeChanger={false}
          showQuickJumper={true}
          showTotal={(total, range) => `${range[0]}-${range[1]} ${t('of')} ${total} ${t('items')}`}
          className="submitted-price-records-table price-table"
          scroll={{ x: 1000 }}
        />
      </section>

      {isLoadingInspectionDetails ? (
        <div className="flex items-center justify-center py-8">
          <Spin size="large" />
        </div>
      ) : selectedInspection ? (
        (() => {
          switch (selectedInspection?.task?.taskType) {
            case TaskTypeEnum.LEGAL_METROLOGY_INSPECTION:
              switch (selectedInspection?.task?.subTaskType) {
                case LegalMetroTaskTypeEnum.SCALE_FORMAT_VERIFICATION:
                  return (
                    <ViewRecordedMonitoringVisitModal
                      formData={selectedInspection?.scaleVerificationDetails}
                      onCancel={handleCloseModal}
                      isModalOpen={isViewModalVisible}
                    />
                  );
                case LegalMetroTaskTypeEnum.PRE_PACKAGED_PRODUCT_VERIFICATION:
                  return (
                    <ViewPrePackagedProductModal
                      formData={selectedInspection?.prePackagedProductVerificationDetails}
                      onCancel={handleCloseModal}
                      isModalOpen={isViewModalVisible}
                    />
                  );
                case LegalMetroTaskTypeEnum.FUEL_DISPENSER_VERIFICATION:
                  return (
                    <ViewRecordedFuelDispenserVerificationModal
                      isModalOpen={isViewModalVisible}
                      formData={selectedInspection.fuelDispenserVerificationDetails}
                      onCancel={handleCloseModal}
                    />
                  );
                default:
                  break;
              }
              break;
            default:
              return (
                <Modal
                  title={t('inspectionDetails')}
                  open={isViewModalVisible}
                  onCancel={handleCloseModal}
                  footer={null}
                  width={1100}
                  centered
                >
                  <div className="submitted-price-records-modal py-4">
                    {(selectedInspection?.respondent || selectedInspection?.task?.respondent) && (
                      <Descriptions
                        bordered
                        column={1}
                        size="middle"
                        labelStyle={{
                          fontWeight: 'bold',
                          backgroundColor: '#f5f5f5',
                          width: '30%',
                        }}
                        className="mb-4"
                      >
                        <Descriptions.Item label={t('respondentName')}>
                          {selectedInspection?.respondent ||
                            selectedInspection?.task?.respondent ||
                            'N/A'}
                        </Descriptions.Item>

                        <Descriptions.Item label={t('price:signatureOfRespondent')}>
                          {selectedInspection?.signatureInitial ||
                            selectedInspection?.task?.signatureInitial ||
                            'N/A'}
                        </Descriptions.Item>

                        <Descriptions.Item label={t('generalObservations')}>
                          {selectedInspection?.finalRemarks ||
                            selectedInspection?.task?.remark ||
                            'N/A'}
                        </Descriptions.Item>
                      </Descriptions>
                    )}

                    <DataTable
                      data={selectedInspection?.prices.flat(2)}
                      columns={viewProductPricecolumns}
                      loading={isLoadingInspections}
                      totalItems={selectedInspection?.prices.length}
                      currentPage={currentPage}
                      pageSize={pageSize}
                      onPageChange={handlePageChange}
                      rowKey="id"
                      showSizeChanger={false}
                      showQuickJumper={true}
                      showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} items`}
                      className="submitted-price-records-table price-table"
                      scroll={{ x: 1000 }}
                    />
                  </div>
                </Modal>
              );
          }
        })()
      ) : (
        <></>
      )}
    </>
  );
};

export default AllView;
